import { 
  isValidPhoneLength, 
  getValidLengths, 
  findCountriesByCode,
  isValidPhoneLengthForCountry,
  CountryPhoneCode
} from './CountryPhoneCodes';

/**
 * Validate email format
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate phone number with country code
 * Validates that the phone number contains only digits and has a valid length
 * for the specified country code.
 * 
 * @param phoneNumber - The phone number to validate (without country code, digits only)
 * @param countryCode - The international dialing code (e.g., "+1", "+90")
 * @returns Object with isValid boolean and error message if invalid
 */
export const isValidPhoneNumberWithCountry = (
  phoneNumber: string,
  countryCode: string
): { isValid: boolean; error?: string } => {
  // Strip any non-digit characters
  const cleanedPhone = phoneNumber.replace(/\D/g, '');
  
  // Check if phone number contains only digits
  if (!/^\d+$/.test(cleanedPhone)) {
    return { isValid: false, error: 'Phone number must contain only digits' };
  }
  
  // Check if phone number is empty
  if (!cleanedPhone || cleanedPhone.trim().length === 0) {
    return { isValid: false, error: 'Phone number is required' };
  }
  
  // Validate length based on country code
  const validLengths = getValidLengths(countryCode);
  const isValid = isValidPhoneLength(cleanedPhone, countryCode);
  
  if (!isValid) {
    const countries = findCountriesByCode(countryCode);
    const countryNames = countries.length > 0 
      ? countries.map(c => c.name).join(' or ')
      : 'selected country';
    return {
      isValid: false,
      error: `Phone number must be ${validLengths.join(' or ')} digits for ${countryNames}`
    };
  }
  
  return { isValid: true };
};

/**
 * Validate phone number with country object
 * More accurate validation when countries share the same dialing code.
 * 
 * @param phoneNumber - The phone number to validate (without country code, digits only)
 * @param country - The country object from CountryPhoneCodes
 * @returns Object with isValid boolean and error message if invalid
 */
export const isValidPhoneNumberForCountry = (
  phoneNumber: string,
  country: CountryPhoneCode | null | undefined
): { isValid: boolean; error?: string } => {
  // Strip any non-digit characters
  const cleanedPhone = phoneNumber.replace(/\D/g, '');
  
  // Check if phone number contains only digits
  if (!/^\d+$/.test(cleanedPhone)) {
    return { isValid: false, error: 'Phone number must contain only digits' };
  }
  
  // Check if phone number is empty
  if (!cleanedPhone || cleanedPhone.trim().length === 0) {
    return { isValid: false, error: 'Phone number is required' };
  }
  
  // Check if country is provided
  if (!country) {
    return { isValid: false, error: 'Country information is required' };
  }
  
  // Validate length based on country
  const isValid = isValidPhoneLengthForCountry(cleanedPhone, country);
  
  if (!isValid) {
    return {
      isValid: false,
      error: `Phone number must be ${country.lengths.join(' or ')} digits for ${country.name}`
    };
  }
  
  return { isValid: true };
};

/**
 * Validate required field
 */
export const isRequired = (value: any): boolean => {
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  return value !== null && value !== undefined;
};

/**
 * Validate event date is in the future
 */
export const isFutureDate = (timestamp: number): boolean => {
  return timestamp > Math.floor(Date.now() / 1000);
};

/**
 * Validate ticket inventory
 */
export const isValidInventory = (inventory: number): boolean => {
  return inventory > 0 && Number.isInteger(inventory);
};

/**
 * Validate name/lastname - only letters and spaces (including Turkish characters)
 */
export const isLettersAndSpaces = (str: string): boolean => {
  return /^[A-Za-zÇĞİÖŞÜçğıöşü\s]+$/.test(str || "");
};

/**
 * Validate phone - only digits
 */
export const isDigitsOnly = (str: string): boolean => {
  return /^\d+$/.test(str || "");
};

/**
 * Validate card number - 16 digits
 */
export const isValidCardNumber = (cardNumber: string): boolean => {
  const cleaned = cardNumber.replace(/\s/g, '');
  return cleaned.length === 16 && /^\d+$/.test(cleaned);
};

/**
 * Validate expiry date - MM / YYYY format
 */
export const isValidExpiry = (expiry: string): boolean => {
  return expiry.length === 9 && /^\d{2}\s\/\s\d{4}$/.test(expiry);
};

/**
 * Validate CVV - minimum 3 digits
 */
export const isValidCVV = (cvv: string): boolean => {
  return cvv.trim().length >= 3 && /^\d+$/.test(cvv);
};

